import { useCallback } from 'react';
import { Direction_Enum, Status_Enum } from '@weave/schema-gen-ts/dist/schemas/fax/shared/v1/enums.pb';
import {
  CountFaxesResponse,
  Fax,
  ListFaxesRequest,
  ListFaxesResponse,
} from '@weave/schema-gen-ts/dist/schemas/fax/v1/fax.pb';
import { Bool_Enum } from '@weave/schema-gen-ts/dist/shared/null/types.pb';
import { InfiniteData, useQueryClient } from 'react-query';
import { FaxQueries } from '@frontend/api-fax';
import { formatPhoneNumberWithoutCountryCode } from '@frontend/phone-numbers';
import { SchemaQueryKey, SchemaQueryRefetchOptions } from '@frontend/react-query-helpers';
import { SchemaFaxService } from '@frontend/schema';
import { ServiceQueries } from '../types';
import { useFaxV1QueryUpdaters } from './use-query-updaters';

type addUpdateCacheParams = {
  faxData?: Fax;
  faxId?: string;
  locationId?: string;
};

const PAGE_DISPLAY_SIZE = 15;

export function useListFaxQueryUpdater() {
  const { updateQuery, invalidateQueries, getEndpointKey } = useFaxV1QueryUpdaters();
  const queryClient = useQueryClient();

  const getFaxById = useCallback(
    (faxId: string, locationId: string) => {
      return queryClient.fetchQuery({
        queryKey: FaxQueries.queryKeys.getFaxForUpdate(faxId),
        queryFn: () =>
          SchemaFaxService.GetFax({
            faxId,
            locationId,
          }),
        cacheTime: 0,
        staleTime: 0,
        retry: 1,
      });
    },
    [queryClient]
  );

  const isMatchingQuery = useCallback((queryRequest: Partial<ListFaxesRequest>, faxData: Fax) => {
    if (!faxData?.id || !queryRequest) return false;

    // Location matching - must match either locationId OR be included in locationIds
    const matchesLocation = queryRequest.locationId === faxData.locationId;
    const matchesLocationIds = !queryRequest.locationIds || queryRequest.locationIds.includes(faxData.locationId);

    // Location is required - early exit if no match
    if (!matchesLocation && !matchesLocationIds) {
      return false;
    }

    // Viewed status matching - A fax is "viewed" if it has viewedAt timestamp
    const isFaxViewed = !!faxData.viewedAt && faxData.direction === Direction_Enum.INBOUND;

    // Special case: If query specifically asks for non-viewed faxes and this fax is not viewed,
    // bypass all other filters and return true (only location must match)
    const matchesIsNotViewed = queryRequest.isViewed === Bool_Enum.FALSE && !isFaxViewed;
    if (matchesIsNotViewed) {
      return true;
    }

    // For all other cases, apply standard filter logic
    const matchesIsViewed =
      queryRequest.isViewed === undefined || (queryRequest.isViewed === Bool_Enum.TRUE && isFaxViewed);

    // Direction matching - undefined means no filter applied
    const matchesDirection = queryRequest.direction === undefined || queryRequest.direction === faxData.direction;

    // Tags matching - fax must have at least one matching tag if tags filter is applied
    const matchesTags =
      !queryRequest.tags ||
      queryRequest.tags.length === 0 ||
      (faxData.tags && faxData.tags.length > 0 && faxData.tags.some((tag) => queryRequest.tags?.includes(tag)));

    // Location phone numbers matching - fax must match one of the filtered fax numbers
    const matchesLocationPhones =
      !queryRequest.locationPhones ||
      queryRequest.locationPhones.length === 0 ||
      queryRequest.locationPhones.includes(faxData.locationPhone);

    // Delivery status matching - comprehensive logic
    const matchesIsDelivered = (() => {
      // No delivery filter = match all
      if (queryRequest.isDelivered === undefined) {
        return true;
      }

      // For inbound faxes, delivery status doesn't apply
      if (faxData.direction === Direction_Enum.INBOUND) {
        // Only match if query doesn't specifically require delivery status
        return queryRequest.isDelivered === Bool_Enum.FALSE;
      }

      // For outbound faxes, determine actual delivery status
      const isActuallyDelivered = faxData.status === Status_Enum.DELIVERED;
      const isActuallyFailed = [Status_Enum.FAILED_FATAL, Status_Enum.FAILED_RETRYABLE].includes(faxData.status);

      if (queryRequest.isDelivered === Bool_Enum.TRUE) {
        return isActuallyDelivered;
      }

      if (queryRequest.isDelivered === Bool_Enum.FALSE) {
        return isActuallyFailed;
      }

      return false;
    })();

    // All filters must match (AND logic)
    return matchesDirection && matchesIsViewed && matchesTags && matchesLocationPhones && matchesIsDelivered;
  }, []);

  const isUnMatchedQuery = useCallback((queryRequest: Partial<ListFaxesRequest>, faxData: Fax) => {
    if (!faxData?.id || !queryRequest) return false;

    // Location matching - must match either locationId OR be included in locationIds
    const matchesLocation = queryRequest.locationId === faxData.locationId;
    const matchesLocationIds = !queryRequest.locationIds || queryRequest.locationIds.includes(faxData.locationId);

    // Location is required - early exit if no match
    if (!matchesLocation && !matchesLocationIds) {
      return false;
    }

    // Check if fax no longer matches the viewed status filter
    const unMatchesIsViewed =
      queryRequest.isViewed !== undefined &&
      ((queryRequest.isViewed === Bool_Enum.TRUE &&
        !faxData.viewedAt &&
        faxData.direction === Direction_Enum.INBOUND) ||
        (queryRequest.isViewed === Bool_Enum.FALSE &&
          !!faxData.viewedAt &&
          faxData.direction === Direction_Enum.INBOUND));

    // Check if fax no longer matches the direction filter
    const unMatchesDirection = queryRequest.direction !== undefined && queryRequest.direction !== faxData.direction;

    // Check if fax no longer matches the delivery status filter
    const unMatchesIsDelivered =
      queryRequest.isDelivered !== undefined &&
      ((queryRequest.isDelivered === Bool_Enum.TRUE && faxData.status !== Status_Enum.DELIVERED) ||
        (queryRequest.isDelivered === Bool_Enum.FALSE && faxData.status === Status_Enum.DELIVERED));

    // Check if fax no longer matches the location phones filter
    const unMatchesLocationPhones = !!(
      queryRequest.locationPhones &&
      queryRequest.locationPhones.length > 0 &&
      !queryRequest.locationPhones.includes(faxData.locationPhone)
    );

    // Check if fax no longer matches the tags filter
    const unMatchesTags = !!(
      queryRequest.tags &&
      queryRequest.tags.length > 0 &&
      !queryRequest.tags.some((tag) => faxData?.tags?.includes(tag))
    );

    // Return true if any filter no longer matches (OR logic for removal conditions)
    return unMatchesIsViewed || unMatchesDirection || unMatchesIsDelivered || unMatchesLocationPhones || unMatchesTags;
  }, []);

  const shouldResetListFaxCache = useCallback(
    (oldData: InfiniteData<ListFaxesResponse>, updatedData: InfiniteData<ListFaxesResponse>) => {
      const isEmpty = updatedData.pages.every((page) => !page.rows || page.rows.length === 0);
      const isOldDataItemsMoreThanDisplaySize =
        oldData.pages.length > 1 || (oldData.pages[0]?.rows && oldData.pages[0]?.rows.length >= PAGE_DISPLAY_SIZE);
      const isUpdatedDataWithOnePageNotFull =
        updatedData.pages.length === 1 &&
        updatedData.pages[0]?.rows &&
        updatedData?.pages[0].rows.length < PAGE_DISPLAY_SIZE;

      return isEmpty || (isOldDataItemsMoreThanDisplaySize && isUpdatedDataWithOnePageNotFull);
    },
    []
  );

  // Insert a fax into sorted pages (createdAt descending). Finds the first row
  // whose createdAt is older than the fax and splices it in before that row.
  // Falls back to appending to the last page if no older row is found.
  const insertFaxSorted = useCallback(
    (data: InfiniteData<ListFaxesResponse>, fax: Fax): InfiniteData<ListFaxesResponse> => {
      if (!data?.pages?.length) {
        return { pages: [{ rows: [fax] }], pageParams: [] };
      }

      const faxCreatedAt = fax.createdAt ?? '';

      for (let pageIdx = 0; pageIdx < data.pages.length; pageIdx++) {
        const page = data.pages[pageIdx];
        if (!page) continue;
        const rows = page.rows ?? [];
        const insertAt = rows.findIndex((r) => (r.createdAt ?? '') < faxCreatedAt);
        if (insertAt !== -1) {
          const newRows = [...rows.slice(0, insertAt), fax, ...rows.slice(insertAt)];
          return {
            ...data,
            pages: data.pages.map((p, i) => (i === pageIdx ? { ...p, rows: newRows } : p)),
          };
        }
      }

      // Fax is older than everything loaded — append to the last page
      const lastIdx = data.pages.length - 1;
      return {
        ...data,
        pages: data.pages.map((page, i) => (i === lastIdx ? { ...page, rows: [...(page.rows ?? []), fax] } : page)),
      };
    },
    []
  );

  const resetListFaxCache = useCallback(
    (queryKey?: SchemaQueryKey<ServiceQueries, 'ListFaxes'>) => {
      queryClient.resetQueries({
        queryKey: queryKey ? queryKey : getEndpointKey({ endpointName: 'ListFaxes' }),
        exact: queryKey ? true : false,
      });
    },
    [queryClient, getEndpointKey]
  );

  const faxExistsInEndpointCache = useCallback(
    (faxId: string, endpointName: 'ListFaxes' | 'ListArchivedFaxes' | 'ListBlockedFaxes'): boolean => {
      const cacheKey = getEndpointKey({ endpointName });
      const queries = queryClient.getQueriesData<InfiniteData<ListFaxesResponse>>(cacheKey);
      return queries.some(([, data]) => data?.pages.some((page) => page.rows?.some((fax) => fax.id === faxId)));
    },
    [queryClient, getEndpointKey]
  );

  // A query is "active" only while a component observes it, so this answers whether any
  // fax list UI is currently mounted.
  const hasActiveListFaxesQuery = useCallback(() => {
    return (
      queryClient.getQueryCache().findAll({
        queryKey: getEndpointKey({ endpointName: 'ListFaxes' }),
        active: true,
      }).length > 0
    );
  }, [queryClient, getEndpointKey]);

  const invalidateListFaxes = useCallback(
    (options?: SchemaQueryRefetchOptions) => {
      invalidateQueries({
        endpointName: 'ListFaxes',
        queryFilters: {
          refetchActive: options?.refetchActive ?? true,
          refetchInactive: options?.refetchInactive ?? false,
        },
      });
    },
    [invalidateQueries]
  );

  const addToListFaxCache = useCallback(
    async ({ faxData, faxId, locationId }: addUpdateCacheParams) => {
      let faxToAdd = faxData;

      if (!faxToAdd) {
        // No identifiers means we can't patch, so refresh whatever is on screen instead.
        if (!faxId || !locationId) {
          invalidateListFaxes();
          return;
        }

        // Nothing is rendering the list, so skip the fetch and let refetchOnMount refresh it later.
        if (!hasActiveListFaxesQuery()) {
          invalidateListFaxes({ refetchActive: false, refetchInactive: false });
          return;
        }

        try {
          faxToAdd = await getFaxById(faxId, locationId);
        } catch {
          invalidateListFaxes();
          return;
        }
      }

      updateQuery<'ListFaxes', true>({
        endpointName: 'ListFaxes',
        queryFilters: {
          predicate: ({ queryKey }) => {
            const request = queryKey[2];
            return isMatchingQuery(request, faxToAdd);
          },
        },
        updater: (data) => insertFaxSorted(data, faxToAdd),
      });
    },
    [updateQuery, insertFaxSorted, invalidateListFaxes, isMatchingQuery, hasActiveListFaxesQuery, getFaxById]
  );

  const updateFaxInListFaxCache = useCallback(
    async ({ faxData, faxId, locationId }: addUpdateCacheParams) => {
      let faxToUpdate = faxData;

      if (!faxToUpdate) {
        // No identifiers means we can't patch, so refresh whatever is on screen instead.
        if (!faxId || !locationId) {
          invalidateListFaxes();
          return;
        }

        // Guarded on cache presence rather than observers (unlike addToListFaxCache): status events are
        // low volume, so patching a warm cache is cheaper than the full refetch a remount would trigger.
        if (!faxExistsInEndpointCache(faxId, 'ListFaxes')) {
          invalidateListFaxes({ refetchActive: false, refetchInactive: false });
          return;
        }

        try {
          faxToUpdate = await getFaxById(faxId, locationId);
        } catch {
          invalidateListFaxes();
          return;
        }
      }

      // update faxData in query caches with the fax existing already.
      updateQuery<'ListFaxes', true>({
        endpointName: 'ListFaxes',
        queryFilters: {
          predicate: ({ queryKey, state }) => {
            const request = queryKey[2];
            const matchesLocation = request.locationId === faxToUpdate?.locationId;
            const matchesLocationIds = !request.locationIds || request.locationIds.includes(faxToUpdate?.locationId);

            const faxExists = !!state.data?.pages.some((page) => page.rows?.some((fax) => fax.id === faxToUpdate?.id));

            return (matchesLocation || matchesLocationIds) && faxExists;
          },
        },
        updater: (data) => ({
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            rows: page.rows?.map((fax) => (fax.id === faxToUpdate?.id ? faxToUpdate : fax)),
          })),
        }),
      });

      // invalidate queries cache with filters that matches the updated faxData but doesn't have the fax in them.
      invalidateQueries<'ListFaxes', true>({
        endpointName: 'ListFaxes',
        queryFilters: {
          predicate: ({ queryKey, state }) => {
            const request = queryKey[2];
            const faxNotExists = !state.data?.pages.some((page) =>
              page.rows?.some((fax) => fax.id === faxToUpdate?.id)
            );
            return faxNotExists && isMatchingQuery(request, faxToUpdate);
          },
        },
      });

      // remove fax from queries cache with filters that doesn't match the updated faxData anymore.
      updateQuery<'ListFaxes', true>({
        endpointName: 'ListFaxes',
        queryFilters: {
          predicate: ({ queryKey, state }) => {
            const request = queryKey[2];
            const faxExists = !!state.data?.pages.some((page) => page.rows?.some((fax) => fax.id === faxToUpdate?.id));
            return faxExists && isUnMatchedQuery(request, faxToUpdate);
          },
        },
        updater: (data, queryKey) => {
          const updatedData = {
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              rows: page.rows?.filter((fax) => fax.id !== faxToUpdate?.id) ?? [],
            })),
          };

          // If the updated cache is empty or if it has only one page and the page is not full,
          // reset query to trigger refetch with empty initial page params
          if (shouldResetListFaxCache(data, updatedData)) {
            setTimeout(() => {
              resetListFaxCache(queryKey);
            }, 0);
          }

          return updatedData;
        },
      });
    },
    [
      updateQuery,
      invalidateQueries,
      invalidateListFaxes,
      isMatchingQuery,
      isUnMatchedQuery,
      shouldResetListFaxCache,
      resetListFaxCache,
      faxExistsInEndpointCache,
      getFaxById,
    ]
  );

  const removeFromListFaxCache = useCallback(
    (faxId: string) => {
      updateQuery<'ListFaxes', true>({
        endpointName: 'ListFaxes',
        queryFilters: {
          predicate: ({ state }) => {
            return state.data?.pages.some((page) => page.rows?.some((fax) => fax.id === faxId)) ?? false;
          },
        },
        updater: (data, queryKey) => {
          const updatedData = {
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              rows: page.rows?.filter((fax) => fax.id !== faxId) ?? [],
            })),
          };

          // If the updated cache is empty or if it has only one page and the page is not full,
          // reset query to trigger refetch with empty initial page params
          if (shouldResetListFaxCache(data, updatedData)) {
            setTimeout(() => {
              resetListFaxCache(queryKey);
            }, 0);
          }

          return updatedData;
        },
      });
    },
    [updateQuery, shouldResetListFaxCache, resetListFaxCache]
  );

  const removeFaxWithContactFromListFaxCache = useCallback(
    (faxNumber: string, locationId: string) => {
      const formattedNumber = formatPhoneNumberWithoutCountryCode(faxNumber);
      updateQuery<'ListFaxes', true>({
        endpointName: 'ListFaxes',
        queryFilters: {
          predicate: ({ state }) => {
            return (
              state.data?.pages.some((page) =>
                page.rows?.some((fax) => fax.contactPhone === formattedNumber && fax.locationId === locationId)
              ) ?? false
            );
          },
        },
        updater: (data, queryKey) => {
          const updatedData = {
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              rows:
                page.rows?.filter((fax) => !(fax.contactPhone === formattedNumber && fax.locationId === locationId)) ??
                [],
            })),
          };

          // If the updated cache is empty or if it has only one page and the page is not full,
          // reset query to trigger refetch with empty initial page params
          if (shouldResetListFaxCache(data, updatedData)) {
            setTimeout(() => {
              resetListFaxCache(queryKey);
            }, 0);
          }

          return updatedData;
        },
      });
    },
    [updateQuery, shouldResetListFaxCache, resetListFaxCache]
  );

  const updateCountField = useCallback(
    (field: keyof CountFaxesResponse, n: number) => {
      queryClient.setQueriesData<CountFaxesResponse | undefined>(
        { queryKey: [...FaxQueries.queryKeys.base, 'count'] },
        (old) => {
          if (!old) return undefined;
          return { ...old, [field]: Math.max(0, old[field] + n) };
        }
      );
    },
    [queryClient]
  );

  const getUnreadCountFromEndpointCache = useCallback(
    (faxIds: string[], endpointName: 'ListFaxes' | 'ListArchivedFaxes' | 'ListBlockedFaxes'): number => {
      const cacheKey = getEndpointKey({ endpointName });
      const queries = queryClient.getQueriesData<InfiniteData<ListFaxesResponse>>(cacheKey);
      const remaining = new Set(faxIds);
      let unreadCount = 0;
      for (const [, data] of queries) {
        for (const page of data?.pages ?? []) {
          for (const fax of page.rows ?? []) {
            if (remaining.delete(fax.id)) {
              if (!fax.viewedAt) unreadCount++;
              if (remaining.size === 0) return unreadCount;
            }
          }
        }
      }
      return unreadCount;
    },
    [queryClient, getEndpointKey]
  );

  const getUnreadCountFromListFaxCache = useCallback(
    (faxIds: string[]) => getUnreadCountFromEndpointCache(faxIds, 'ListFaxes'),
    [getUnreadCountFromEndpointCache]
  );

  const getUnreadCountFromListArchivedFaxCache = useCallback(
    (faxIds: string[]) => getUnreadCountFromEndpointCache(faxIds, 'ListArchivedFaxes'),
    [getUnreadCountFromEndpointCache]
  );

  const adjustFaxCountForArchive = useCallback(
    (faxIds: string[], isArchived: boolean) => {
      const unreadCount = isArchived
        ? getUnreadCountFromListFaxCache(faxIds)
        : getUnreadCountFromListArchivedFaxCache(faxIds);
      if (unreadCount === 0) return;
      if (isArchived) {
        updateCountField('faxCount', -unreadCount);
        updateCountField('archivedFaxCount', unreadCount);
      } else {
        updateCountField('archivedFaxCount', -unreadCount);
        updateCountField('faxCount', unreadCount);
      }
    },
    [getUnreadCountFromListFaxCache, getUnreadCountFromListArchivedFaxCache, updateCountField]
  );

  const removeFromEndpointCache = useCallback(
    (
      faxId: string,
      endpointName: 'ListArchivedFaxes' | 'ListBlockedFaxes',
      countField: 'archivedFaxCount' | 'blockedFaxCount'
    ) => {
      const wasUnread = getUnreadCountFromEndpointCache([faxId], endpointName) > 0;

      updateQuery<typeof endpointName, true>({
        endpointName,
        queryFilters: {
          predicate: ({ state }) =>
            state.data?.pages.some((page) => page.rows?.some((fax) => fax.id === faxId)) ?? false,
        },
        updater: (data) => ({
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            rows: page.rows?.filter((fax) => fax.id !== faxId) ?? [],
          })),
        }),
      });

      if (wasUnread) {
        updateCountField(countField, -1);
      }
    },
    [updateQuery, getUnreadCountFromEndpointCache, updateCountField]
  );

  const removeFromListArchivedFaxCache = useCallback(
    (faxId: string) => removeFromEndpointCache(faxId, 'ListArchivedFaxes', 'archivedFaxCount'),
    [removeFromEndpointCache]
  );

  const removeFromListBlockedFaxCache = useCallback(
    (faxId: string) => removeFromEndpointCache(faxId, 'ListBlockedFaxes', 'blockedFaxCount'),
    [removeFromEndpointCache]
  );

  const invalidateListArchivedFaxes = useCallback(() => {
    invalidateQueries({
      endpointName: 'ListArchivedFaxes',
    });
  }, [invalidateQueries]);

  const invalidateListBlockedFaxes = useCallback(() => {
    invalidateQueries({
      endpointName: 'ListBlockedFaxes',
    });
  }, [invalidateQueries]);

  const getFaxesWithContactFromEndpointCache = useCallback(
    (
      faxNumber: string,
      locationId: string,
      endpointName: 'ListFaxes' | 'ListArchivedFaxes' | 'ListBlockedFaxes'
    ): Fax[] => {
      const formattedPhone = formatPhoneNumberWithoutCountryCode(faxNumber);
      const cacheKey = getEndpointKey({ endpointName });
      const queries = queryClient.getQueriesData<InfiniteData<ListFaxesResponse>>(cacheKey);
      const faxes: Fax[] = [];
      for (const [, data] of queries) {
        for (const page of data?.pages ?? []) {
          for (const fax of page.rows ?? []) {
            if (fax.contactPhone === formattedPhone && fax.locationId === locationId) {
              faxes.push(fax);
            }
          }
        }
      }
      return faxes;
    },
    [queryClient, getEndpointKey]
  );

  const getFaxesWithContactFromListFaxCache = useCallback(
    (faxNumber: string, locationId: string) => getFaxesWithContactFromEndpointCache(faxNumber, locationId, 'ListFaxes'),
    [getFaxesWithContactFromEndpointCache]
  );

  const removeFaxesWithContactFromListBlockedFaxCache = useCallback(
    (faxNumber: string, locationId: string) => {
      const formattedPhone = formatPhoneNumberWithoutCountryCode(faxNumber);
      updateQuery<'ListBlockedFaxes', true>({
        endpointName: 'ListBlockedFaxes',
        queryFilters: {
          predicate: ({ state }) =>
            state.data?.pages.some((page) =>
              page.rows?.some((fax) => fax.contactPhone === formattedPhone && fax.locationId === locationId)
            ) ?? false,
        },
        updater: (data) => ({
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            rows:
              page.rows?.filter((fax) => !(fax.contactPhone === formattedPhone && fax.locationId === locationId)) ?? [],
          })),
        }),
      });
    },
    [updateQuery]
  );

  const addToEndpointCache = useCallback(
    (faxData: Fax, endpointName: 'ListArchivedFaxes' | 'ListBlockedFaxes') => {
      updateQuery<typeof endpointName, true>({
        endpointName,
        queryFilters: {
          predicate: ({ queryKey }) => {
            const request = queryKey[2];
            return (
              request?.locationId === faxData.locationId ||
              (request?.locationIds?.includes(faxData.locationId) ?? false)
            );
          },
        },
        updater: (data) => insertFaxSorted(data, faxData),
      });
    },
    [updateQuery, insertFaxSorted]
  );

  const getFaxesByIdsFromEndpointCache = useCallback(
    (faxIds: string[], endpointName: 'ListFaxes' | 'ListArchivedFaxes' | 'ListBlockedFaxes'): Fax[] => {
      const cacheKey = getEndpointKey({ endpointName });
      const queries = queryClient.getQueriesData<InfiniteData<ListFaxesResponse>>(cacheKey);
      const remaining = new Set(faxIds);
      const faxes: Fax[] = [];
      for (const [, data] of queries) {
        for (const page of data?.pages ?? []) {
          for (const fax of page.rows ?? []) {
            // `delete` returns true if the id was in `remaining` (and removes it),
            // so this both filters to requested faxes and prevents double-counting.
            if (remaining.delete(fax.id)) {
              faxes.push(fax);
              // Found everything we were asked about — no need to keep scanning the cache.
              if (remaining.size === 0) return faxes;
            }
          }
        }
      }
      return faxes;
    },
    [queryClient, getEndpointKey]
  );

  const addFaxesToListArchivedFaxCache = useCallback(
    (faxes: Fax[]) => {
      faxes.forEach((fax) => addToEndpointCache(fax, 'ListArchivedFaxes'));
    },
    [addToEndpointCache]
  );

  const addFaxesToListBlockedFaxCache = useCallback(
    (faxes: Fax[]) => {
      faxes.forEach((fax) => addToEndpointCache(fax, 'ListBlockedFaxes'));
    },
    [addToEndpointCache]
  );

  const updateFaxInEndpointCache = useCallback(
    async (
      { faxData, faxId, locationId }: addUpdateCacheParams,
      endpointName: 'ListArchivedFaxes' | 'ListBlockedFaxes'
    ) => {
      // Skip the API fetch entirely if the fax isn't in this endpoint's cache
      if (!faxData && faxId && !faxExistsInEndpointCache(faxId, endpointName)) return;

      let faxToUpdate = faxData;
      try {
        if (!faxToUpdate) {
          if (faxId && locationId) {
            faxToUpdate = await getFaxById(faxId, locationId);
          } else {
            throw Error();
          }
        }
      } catch {
        if (endpointName === 'ListArchivedFaxes') {
          invalidateListArchivedFaxes();
        } else {
          invalidateListBlockedFaxes();
        }
        return;
      }
      updateQuery<typeof endpointName, true>({
        endpointName,
        queryFilters: {
          predicate: ({ state }) =>
            state.data?.pages.some((page) => page.rows?.some((fax) => fax.id === faxToUpdate?.id)) ?? false,
        },
        updater: (data) => ({
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            rows: page.rows?.map((fax) => (faxToUpdate && fax.id === faxToUpdate?.id ? faxToUpdate : fax)) ?? [],
          })),
        }),
      });
    },
    [updateQuery, getFaxById, faxExistsInEndpointCache, invalidateListArchivedFaxes, invalidateListBlockedFaxes]
  );

  const updateFaxInListArchivedFaxCache = useCallback(
    (params: addUpdateCacheParams) => updateFaxInEndpointCache(params, 'ListArchivedFaxes'),
    [updateFaxInEndpointCache]
  );

  const updateFaxInListBlockedFaxCache = useCallback(
    (params: addUpdateCacheParams) => updateFaxInEndpointCache(params, 'ListBlockedFaxes'),
    [updateFaxInEndpointCache]
  );

  const updateFaxReadStatusInAllCaches = useCallback(
    (faxId: string, markedRead: boolean) => {
      const newViewedAt = markedRead ? new Date().toISOString() : '';

      // Read inbox state BEFORE mutating (for accurate count delta)
      const isInInbox = faxExistsInEndpointCache(faxId, 'ListFaxes');
      const wasUnreadInInbox = isInInbox && getUnreadCountFromEndpointCache([faxId], 'ListFaxes') > 0;

      const endpointNames = ['ListFaxes', 'ListArchivedFaxes', 'ListBlockedFaxes'] as const;
      for (const endpointName of endpointNames) {
        updateQuery<typeof endpointName, true>({
          endpointName,
          queryFilters: {
            predicate: ({ state }) =>
              state.data?.pages.some((page) => page.rows?.some((fax) => fax.id === faxId)) ?? false,
          },
          updater: (data) => ({
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              rows: page.rows?.map((fax) => (fax.id === faxId ? { ...fax, viewedAt: newViewedAt } : fax)) ?? [],
            })),
          }),
        });
      }

      // archivedFaxCount / blockedFaxCount are not affected by read status — only faxCount is
      if (isInInbox) {
        if (markedRead && wasUnreadInInbox) updateCountField('faxCount', -1);
        else if (!markedRead && !wasUnreadInInbox) updateCountField('faxCount', 1);
      }
    },
    [updateQuery, faxExistsInEndpointCache, getUnreadCountFromEndpointCache, updateCountField]
  );

  return {
    addToListFaxCache,
    getFaxesByIdsFromEndpointCache,
    addFaxesToListArchivedFaxCache,
    addFaxesToListBlockedFaxCache,
    updateFaxInListFaxCache,
    updateFaxInListArchivedFaxCache,
    updateFaxInListBlockedFaxCache,
    removeFromListFaxCache,
    removeFromListArchivedFaxCache,
    removeFromListBlockedFaxCache,
    removeFaxWithContactFromListFaxCache,
    getFaxesWithContactFromListFaxCache,
    getFaxesWithContactFromEndpointCache,
    removeFaxesWithContactFromListBlockedFaxCache,
    updateCountField,
    updateFaxReadStatusInAllCaches,
    adjustFaxCountForArchive,
    getUnreadCountFromListFaxCache,
    getUnreadCountFromListArchivedFaxCache,
    invalidateListFaxes,
    invalidateListArchivedFaxes,
    invalidateListBlockedFaxes,
  };
}
